import { motion } from "framer-motion";
import SpeakButton from "@/components/SpeakButton";

/**
 * The "Spoken Contract" view — the moment Stay AWAKE earns its name.
 * Reformats the 90-day plan into first-person commitment statements
 * designed to be read aloud to a partner during a workshop.
 */
export default function SpokenContract({ plan, refinedProblem, firstName }) {
  const cleanName = (firstName || "").trim();
  const namePart = cleanName ? `, ${cleanName},` : "";

  const lines = [
    {
      key: "honest",
      lead: cleanName ? `I, ${cleanName},` : "I",
      body: `have looked honestly at "${refinedProblem}".`,
    },
    {
      key: "test",
      lead: "In the next 30 days, I will test —",
      body: plan.days_30,
    },
    {
      key: "build",
      lead: "By day 60, I will have built —",
      body: plan.days_60,
    },
    {
      key: "commit",
      lead: "By day 90, I commit to —",
      body: plan.days_90,
    },
    {
      key: "fail",
      lead: "I know this might fail because —",
      body: plan.might_fail,
    },
  ];

  const fullText = lines
    .map((l) => `${l.lead} ${l.body}`)
    .join(". ")
    .replace(/\.\./g, ".");

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      className="weave-contract"
      data-testid="spoken-contract"
    >
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p
            className="text-[11px] uppercase tracking-[0.22em] font-medium"
            style={{ color: "#3c8386" }}
          >
            Your spoken contract
          </p>
          <h2 className="mt-3 text-3xl md:text-4xl font-light text-slate-800 tracking-tight leading-tight">
            Find a partner.
            <br />
            Take turns reading this aloud.
          </h2>
        </div>
        <SpeakButton
          text={fullText}
          label="Read the contract aloud"
          testid="contract-speak-btn"
        />
      </div>

      <div className="weave-contract-statements" data-testid="contract-statements">
        {lines.map((line, idx) => (
          <motion.p
            key={line.key}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{
              duration: 0.45,
              delay: idx * 0.07,
              ease: [0.22, 1, 0.36, 1],
            }}
          >
            <span className="weave-contract-lead">{line.lead}</span>{" "}
            <span className="weave-contract-body">{line.body}</span>
          </motion.p>
        ))}
      </div>

      <div className="weave-contract-footer">
        <p className="text-sm text-slate-500">
          When you've both read aloud, you have made each other a quiet promise.
          That's the contract.
        </p>
      </div>
    </motion.div>
  );
}

/**
 * Add the spoken contract as a separate page in the PDF export.
 * Mirrors the structure used in PlanOutput.jsx#buildPdf.
 */
export function appendContractToPdf(doc, { plan, refinedProblem, firstName }) {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 56;
  const contentWidth = pageWidth - margin * 2;

  doc.addPage();
  let y = margin;

  // Brand mark
  doc.setFillColor(74, 160, 164);
  doc.circle(margin + 4, margin + 4, 4, "F");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(60, 131, 134);
  doc.text("THE WEAVE  ·  STAY AWAKE", margin + 16, margin + 8);
  y = margin + 56;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(28);
  doc.setTextColor(30, 41, 59);
  doc.text("Your Spoken Contract", margin, y);
  y += 22;

  doc.setFontSize(11);
  doc.setTextColor(100, 116, 139);
  doc.text("Find a partner. Take turns reading this aloud.", margin, y);
  y += 30;

  // Thin teal rule
  doc.setDrawColor(74, 160, 164);
  doc.setLineWidth(0.6);
  doc.line(margin, y, pageWidth - margin, y);
  y += 24;

  const cleanName = (firstName || "").trim();
  const lines = [
    {
      lead: cleanName ? `I, ${cleanName},` : "I",
      body: `have looked honestly at "${refinedProblem}".`,
    },
    { lead: "In the next 30 days, I will test —", body: plan.days_30 },
    { lead: "By day 60, I will have built —", body: plan.days_60 },
    { lead: "By day 90, I commit to —", body: plan.days_90 },
    { lead: "I know this might fail because —", body: plan.might_fail },
  ];

  lines.forEach((line) => {
    if (y > pageHeight - margin - 60) {
      doc.addPage();
      y = margin;
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(60, 131, 134);
    const leadLines = doc.splitTextToSize(line.lead, contentWidth);
    leadLines.forEach((ll) => {
      doc.text(ll, margin, y);
      y += 17;
    });
    y += 2;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(13);
    doc.setTextColor(30, 41, 59);
    const bodyLines = doc.splitTextToSize(line.body, contentWidth);
    bodyLines.forEach((bl) => {
      if (y > pageHeight - margin - 30) {
        doc.addPage();
        y = margin;
      }
      doc.text(bl, margin, y);
      y += 19;
    });
    y += 16;
  });

  // Closing line
  if (y > pageHeight - margin - 60) {
    doc.addPage();
    y = margin;
  }
  doc.setFont("helvetica", "italic");
  doc.setFontSize(11);
  doc.setTextColor(100, 116, 139);
  const closing = doc.splitTextToSize(
    "When you've both read aloud, you have made each other a quiet promise. That's the contract.",
    contentWidth
  );
  closing.forEach((cl) => {
    doc.text(cl, margin, y);
    y += 16;
  });
}
